import React, { useCallback, useMemo } from 'react'
import { AsyncSelect, Field } from '@workwhile/ui'
import { FullOptions, MatchData, Searcher } from 'fast-fuzzy'
import { useCompanyLocationsQuery } from 'queries/company/useCompanyLocationsQuery'

type LocationOption = { label: string; value: number }

const fuzzyOptions: FullOptions<LocationOption> = {
  threshold: 0.7,
  ignoreCase: true,
  ignoreSymbols: true,
  normalizeWhitespace: true,
  returnMatchData: false,
  keySelector: (obj) => [obj.label, obj.value.toString()],
}

type LocationPickerProps = {
  value?: number | null
  onChange: (value: number | undefined) => void
  companyId?: number | null
  error?: string
  label?: React.ReactNode
}

export const LocationPicker = ({
  value,
  onChange,
  companyId,
  error,
  label = 'Location',
}: LocationPickerProps) => {
  const { data: locations } = useCompanyLocationsQuery(companyId ?? undefined)

  const locationOptions = useMemo(() => {
    return [...(locations ?? [])]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((location) => ({
        label: `${location.name} (${location.id})`,
        value: Number(location.id),
      }))
  }, [locations])

  const searcher = useMemo(() => {
    return new Searcher<LocationOption, FullOptions<LocationOption>>(
      locationOptions,
      fuzzyOptions
    )
  }, [locationOptions])

  const loadOptions = useCallback(
    (rawInput: string) => {
      if (!rawInput) return true

      const results = searcher.search(
        rawInput,
        fuzzyOptions
      ) as unknown as MatchData<LocationOption>[]

      return new Promise((resolve) => {
        resolve(results)
      })
    },
    [searcher]
  )

  return (
    <Field label={label} error={error}>
      <AsyncSelect
        block
        isClearable
        isSearchable
        isDisabled={!companyId}
        placeholder={companyId ? 'Select a location' : 'Select a company first'}
        defaultOptions={locationOptions}
        loadOptions={loadOptions}
        menuPortalTarget={document.body}
        value={locationOptions.find((option) => option.value === value) || null}
        onChange={(option) => {
          onChange(option?.value ?? undefined)
        }}
        menuPosition="fixed"
        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 10011,
            pointerEvents: 'auto',
          }),
        }}
      />
    </Field>
  )
}
